import { useTranslation } from "react-i18next";
import { ArrowDownLeft } from "lucide-react";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { buildReportMessagesExportPath } from "../../api/reports-api.js";
import { isApiError } from "../../api/client.js";
import { ErrorState } from "../../components/common/ErrorState/index.js";
import { incomingReportEmptyKind } from "./report-empty-kind.js";
import { INCOMING_REPORT_COLUMNS, type IncomingReportColumnId } from "./report-column-defs.js";
import { IncomingReportSummary } from "./IncomingReportSummary.js";
import { IncomingReportTable } from "./IncomingReportTable.js";
import { IncomingReportToolbar } from "./IncomingReportToolbar.js";
import { MessageDetailDrawer } from "./MessageDetailDrawer.js";
import { ReportColumnsPopover } from "./ReportColumnsPopover.js";
import { ReportExportMenu } from "./ReportExportMenu.js";
import { ReportPageChrome } from "./ReportPageChrome.js";
import { ReportPageHeader } from "./ReportPageHeader.js";
import { ReportRefreshButton } from "./ReportRefreshButton.js";
import { useIncomingMessagesReport } from "./use-incoming-messages-report.js";
import { useReportMessageDetail } from "./use-report-message-detail.js";
import { useReportTableLayout } from "./use-report-table-layout.js";
import { exportLocaleFromUi, useReportExport } from "./use-report-export.js";
import type { IncomingReportFilterValues } from "./IncomingReportFilters.js";

function incomingColumnLabel(t: (key: string) => string, id: IncomingReportColumnId): string {
  switch (id) {
    case "platform":
      return t("reports.incoming.columns.channel");
    case "account":
      return t("reports.incoming.columns.endpoint");
    default:
      return t(`reports.incoming.columns.${id}`);
  }
}

export function IncomingMessagesReportPage({
  organizationId,
  role,
}: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const report = useIncomingMessagesReport({ organizationId, role });
  const detail = useReportMessageDetail({
    organizationId,
    canAccess: report.canAccess,
  });
  const exportCtl = useReportExport();
  const layout = useReportTableLayout(INCOMING_REPORT_COLUMNS);

  const onFiltersChange = (patch: Partial<IncomingReportFilterValues>) => {
    if ("platform" in patch) report.setPlatform(patch.platform);
    if ("endpointId" in patch) report.setEndpointId(patch.endpointId);
    if ("messageType" in patch) report.setMessageType(patch.messageType);
  };

  const onExport = (format: "csv" | "xlsx") => {
    const path = buildReportMessagesExportPath(organizationId, {
      direction: "INBOUND",
      fromDate: report.query.from,
      toDate: report.query.to,
      search: report.query.search || undefined,
      platform: report.query.platform,
      endpointId: report.query.endpointId,
      messageType: report.query.messageType,
      sort: report.query.sort,
      format,
      locale: exportLocaleFromUi(i18n.language),
    });
    void exportCtl.runExport({
      pathWithQuery: path,
      fallbackFilename: `incoming-messages.${format}`,
    });
  };

  const errorMessage =
    report.error == null
      ? null
      : isApiError(report.error) && report.error.status === 0
        ? t("reports.incoming.backendUnavailable")
        : isApiError(report.error) && report.error.status === 403
          ? t("reports.incoming.forbidden")
          : t("reports.incoming.loadFailed");

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden"
      data-testid="reports-incoming-page"
    >
      <ReportPageChrome>
        <ReportPageHeader
          icon={ArrowDownLeft}
          title={t("reports.incoming.title")}
          description={t("reports.incoming.helper")}
          actions={
            report.canAccess ? (
              <div className="flex items-center gap-2">
                <ReportRefreshButton
                  refreshing={Boolean(report.loading && report.list)}
                  disabled={report.loading}
                  onRefresh={() => {
                    report.refresh();
                    if (detail.messageId != null) detail.refresh();
                  }}
                  testId="reports-incoming-refresh"
                />
                <div className="grid gap-1">
                  <ReportExportMenu
                    exporting={exportCtl.exporting}
                    onExport={onExport}
                    testId="reports-incoming-export"
                  />
                  {exportCtl.exportError ? (
                    <p className="m-0 text-[0.75rem] text-danger" role="alert">
                      {exportCtl.exportError}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null
          }
        />

        <IncomingReportToolbar
          query={report.query}
          dateFrom={report.dateFrom}
          dateTo={report.dateTo}
          options={report.options}
          filteredTotal={report.list?.totalCount ?? null}
          filtersActive={report.filtersActive}
          onSearchChange={report.setSearch}
          onDateRangeChange={report.setDateRange}
          onFiltersChange={onFiltersChange}
          onResetFilters={report.resetFilters}
          columnsSlot={
            <ReportColumnsPopover
              columnOrder={layout.columnOrder}
              hiddenColumns={layout.hiddenColumns}
              requiredColumns={INCOMING_REPORT_COLUMNS.requiredColumns}
              labelFor={(id) => incomingColumnLabel(t, id)}
              onToggle={layout.toggleColumn}
              onMove={layout.moveColumn}
              onReorder={layout.reorderColumn}
              onReset={layout.resetColumns}
              testIdPrefix="reports-incoming"
            />
          }
        />
      </ReportPageChrome>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-3 md:overflow-hidden md:px-5">
        {errorMessage ? (
          <ErrorState
            layout="panel"
            message={errorMessage}
            onRetry={report.refresh}
            retryLabel={t("auth.retry")}
          />
        ) : null}

        {report.summary ? (
          <IncomingReportSummary
            summary={report.summary}
            platform={report.query.platform}
            onSelectPlatform={(platform) => report.setPlatform(platform ?? undefined)}
          />
        ) : report.loading ? (
          <div
            className="grid shrink-0 grid-cols-2 gap-1.5 sm:grid-cols-4"
            data-testid="reports-incoming-summary-loading"
          >
            {Array.from({ length: 4 }, (_, index) => (
              <div
                key={index}
                className="h-12 animate-pulse rounded-md border border-line/70 bg-surface-muted motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : null}

        <div className="flex min-h-[20rem] min-h-0 flex-1 flex-col md:min-h-0">
          <IncomingReportTable
            list={report.list}
            loading={report.loading}
            page={report.query.page}
            pageSize={report.query.pageSize}
            emptyKind={incomingReportEmptyKind(report.query)}
            visibleColumns={layout.visibleColumns}
            widths={layout.widths}
            onResizeColumn={layout.resizeColumn}
            onReorderColumn={layout.reorderColumn}
            onPageChange={report.setPage}
            onPageSizeChange={report.setPageSize}
            onResetFilters={report.resetFilters}
            onOpenMessage={detail.openMessage}
          />
        </div>
      </div>

      <MessageDetailDrawer
        open={detail.messageId != null}
        detail={detail.detail}
        loading={detail.loading}
        error={detail.error}
        onClose={detail.closeMessage}
        onRetry={detail.refresh}
      />
    </div>
  );
}
